<template>
    <div>
        <Head :title="__('Ratings & Reviews')" />
        <div v-if="loading" class="fixed inset-0 bg-gray-500 bg-opacity-50 flex items-center justify-center z-50">
    <div class="bg-white p-4 rounded-lg shadow-lg">
        <p>{{ __('Loading...') }}</p>
    </div>
</div>

        <!-- Search and Filter Section -->
        <div class="mb-8 bg-white rounded-xl shadow-lg p-6">
            <div class="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div class="flex-1">
                    <div class="relative">
                        <div class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                            <icon name="search" class="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                            type="text"
                            v-model="searchFilter"
                            class="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                            :placeholder="__('Search feedback...')"
                        >
                    </div>
                </div>
                <div class="flex gap-3">
                    <select v-model="selectedRatingFilter" class="border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500">
                        <option value="all">{{ __('All Ratings') }}</option>
                        <option value="5">{{ __('5 Stars') }}</option>
                        <option value="4">{{ __('4 Stars') }}</option>
                        <option value="3">{{ __('3 Stars') }}</option>
                        <option value="2">{{ __('2 Stars') }}</option>
                        <option value="1">{{ __('1 Star') }}</option>
                    </select>
                    <select v-model="selectedChannelFilter" class="border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500">
                        <option value="all">{{ __('All Channels') }}</option>
                        <option value="chat">{{ __('Chat') }}</option>
                        <option value="ticket">{{ __('Ticket') }}</option>
                        <option value="whatsapp">{{ __('WhatsApp') }}</option>
                    </select>
                </div>
            </div>
        </div>

        <!-- Ratings & Reviews Grid -->
        <section class="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            <!-- Chat Ratings -->
            <div class="bg-white rounded-xl shadow-lg p-6">
                <h2 class="font-semibold text-xl mb-6 text-gray-900">{{ __('Chat Ratings') }}</h2>

                <!-- Average Rating Card -->
                <div class="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-6 mb-6">
                    <div class="text-center">
                        <h3 class="text-sm font-medium text-gray-600 mb-2">{{ __('Average Rating') }}</h3>
                        <p class="text-4xl font-bold text-gray-900 mb-2">
                            {{ average_chat_rating ? parseFloat(average_chat_rating).toFixed(1) : '0.0' }}
                            <span class="text-xl text-gray-500">/5</span>
                        </p>
                        <div class="flex justify-center items-center">
                            <span v-for="star in 5" :key="'chat-avg-'+star" class="text-yellow-400 text-lg">
                                <icon v-if="star <= Math.round(average_chat_rating || 0)" name="star" class="h-5 w-5 fill-current inline" />
                                <icon v-else name="star" class="h-5 w-5 text-gray-300 inline" />
                            </span>
                        </div>
                    </div>
                </div>

                <!-- Rating Distribution -->
                <div class="mb-6">
                    <h3 class="font-medium text-gray-900 mb-4">{{ __('Rating Distribution') }}</h3>
                    <div v-if="chat_rating_distribution && total_chat_ratings > 0" class="space-y-2">
                        <div v-for="rating in [5, 4, 3, 2, 1]" :key="'chat-dist-'+rating" class="flex items-center">
                            <span class="w-8 flex items-center text-sm">
                                <span class="mr-1">{{ rating }}</span>
                                <span class="text-yellow-400">★</span>
                            </span>
                            <div class="flex-1 h-3 bg-gray-200 rounded-full mx-3">
                                <div
                                    class="h-3 bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-full transition-all duration-500 ease-out rating-bar"
                                    :style="{ width: `${((chat_rating_distribution[rating] || 0) / total_chat_ratings) * 100}%` }"
                                ></div>
                            </div>
                            <span class="w-8 text-sm text-gray-600">{{ chat_rating_distribution[rating] || 0 }}</span>
                        </div>
                    </div>
                    <div v-else class="text-sm text-gray-500 text-center py-4">{{ __('No ratings yet') }}</div>
                </div>

                <!-- Recent Ratings -->
                <div>
                    <h3 class="font-medium text-gray-900 mb-3">{{ __('Recent Ratings') }}</h3>
                    <div v-if="filteredChatRatings && filteredChatRatings.length" class="max-h-64 overflow-y-auto space-y-3 pr-2 scrollbar-hide">
                        <div v-for="rating in filteredChatRatings" :key="'chat-rating-'+rating.id"
                             class="bg-gray-50 rounded-lg p-3 border border-gray-100 hover:shadow-md transition-shadow duration-200">
                            <div class="flex items-center justify-between mb-2">
                                <div class="flex items-center">
                                    <span class="text-yellow-400 flex">
                                        <template v-for="star in 5" :key="'recent-'+rating.id+'-'+star">
                                            <span v-if="star <= rating.rating" class="text-yellow-400">★</span>
                                            <span v-else class="text-gray-300">★</span>
                                        </template>
                                    </span>
                                    <span class="text-sm text-gray-700 ml-2">{{ rating.rating }}/5</span>
                                </div>
                                <span class="text-xs text-gray-500">{{ formatDate(rating.rated_at) }}</span>
                            </div>
                            <p class="text-sm text-gray-700" v-if="rating.comment">{{ rating.comment }}</p>
                        </div>
                    </div>
                    <p v-else class="text-sm text-gray-500 text-center py-4">{{ __('No recent ratings') }}</p>
                </div>
            </div>

            <!-- Ticket Reviews -->
            <div class="bg-white rounded-xl shadow-lg p-6">
                <h2 class="font-semibold text-xl mb-6 text-gray-900">{{ __('Ticket Reviews') }}</h2>

                <!-- Average Rating Card -->
                <div class="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200 p-6 mb-6">
                    <div class="text-center">
                        <h3 class="text-sm font-medium text-gray-600 mb-2">{{ __('Average Rating') }}</h3>
                        <p class="text-4xl font-bold text-gray-900 mb-2">
                            {{ average_ticket_rating ? parseFloat(average_ticket_rating).toFixed(1) : '0.0' }}
                            <span class="text-xl text-gray-500">/5</span>
                        </p>
                        <div class="flex justify-center items-center">
                            <span v-for="star in 5" :key="'ticket-avg-'+star" class="text-yellow-400 text-lg">
                                <icon v-if="star <= Math.round(average_ticket_rating || 0)" name="star" class="h-5 w-5 fill-current inline" />
                                <icon v-else name="star" class="h-5 w-5 text-gray-300 inline" />
                            </span>
                        </div>
                    </div>
                </div>

                <!-- Rating Distribution -->
                <div class="mb-6">
                    <h3 class="font-medium text-gray-900 mb-4">{{ __('Rating Distribution') }}</h3>
                    <div v-if="ticket_rating_distribution && total_ticket_reviews > 0" class="space-y-2">
                        <div v-for="rating in [5, 4, 3, 2, 1]" :key="'ticket-dist-'+rating" class="flex items-center">
                            <span class="w-8 flex items-center text-sm">
                                <span class="mr-1">{{ rating }}</span>
                                <span class="text-yellow-400">★</span>
                            </span>
                            <div class="flex-1 h-3 bg-gray-200 rounded-full mx-3">
                                <div
                                    class="h-3 bg-gradient-to-r from-green-400 to-green-500 rounded-full transition-all duration-500 ease-out rating-bar"
                                    :style="{ width: `${((ticket_rating_distribution[rating] || 0) / total_ticket_reviews) * 100}%` }"
                                ></div>
                            </div>
                            <span class="w-8 text-sm text-gray-600">{{ ticket_rating_distribution[rating] || 0 }}</span>
                        </div>
                    </div>
                    <div v-else class="text-sm text-gray-500 text-center py-4">{{ __('No reviews yet') }}</div>
                </div>

                <!-- Recent Reviews -->
                <div>
                    <h3 class="font-medium text-gray-900 mb-3">{{ __('Recent Reviews') }}</h3>
                    <div v-if="filteredTicketReviews && filteredTicketReviews.length" class="max-h-64 overflow-y-auto space-y-3 pr-2 scrollbar-hide">
                        <div v-for="review in filteredTicketReviews" :key="'ticket-review-'+review.id"
                             class="bg-gray-50 rounded-lg p-3 border border-gray-100 hover:shadow-md transition-shadow duration-200">
                            <div class="flex items-center justify-between mb-2">
                                <div class="flex items-center">
                                    <span class="text-yellow-400 flex">
                                        <template v-for="star in 5" :key="'review-'+review.id+'-'+star">
                                            <span v-if="star <= review.rating" class="text-yellow-400">★</span>
                                            <span v-else class="text-gray-300">★</span>
                                        </template>
                                    </span>
                                    <span class="text-sm text-gray-700 ml-2">{{ review.rating }}/5</span>
                                </div>
                                <span class="text-xs text-gray-500">{{ formatDate(review.created_at) }}</span>
                            </div>
                            <p class="text-sm text-gray-700 mb-2" v-if="review.comment">{{ review.comment }}</p>
                            <div class="text-xs text-gray-500 space-y-1">
                                <p v-if="review.ticket">{{ __('Ticket') }}: {{ review.ticket.subject }}</p>
                                <p v-if="review.user">{{ __('By') }}: {{ review.user.name }}</p>
                            </div>
                        </div>
                    </div>
                    <p v-else class="text-sm text-gray-500 text-center py-4">{{ __('No recent reviews') }}</p>
                </div>
            </div>

            <!-- WhatsApp Ratings -->
            <div class="bg-white rounded-xl shadow-lg p-6">
                <h2 class="font-semibold text-xl mb-6 text-gray-900">{{ __('WhatsApp Ratings') }}</h2>

                <!-- Average Rating Card -->
                <div class="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200 p-6 mb-6">
                    <div class="text-center">
                        <h3 class="text-sm font-medium text-gray-600 mb-2">{{ __('Average Rating') }}</h3>
                        <p class="text-4xl font-bold text-gray-900 mb-2">
                            {{ whatsapp_average_rating ? parseFloat(whatsapp_average_rating).toFixed(1) : '0.0' }}
                            <span class="text-xl text-gray-500">/5</span>
                        </p>
                        <div class="flex justify-center items-center">
                            <span v-for="star in 5" :key="'whatsapp-avg-'+star" class="text-yellow-400 text-lg">
                                <icon v-if="star <= Math.round(whatsapp_average_rating || 0)" name="star" class="h-5 w-5 fill-current inline" />
                                <icon v-else name="star" class="h-5 w-5 text-gray-300 inline" />
                            </span>
                        </div>
                    </div>
                </div>

                <!-- Rating Distribution -->
                <div class="mb-6">
                    <h3 class="font-medium text-gray-900 mb-4">{{ __('Rating Distribution') }}</h3>
                    <div v-if="whatsapp_rating_distribution && total_whatsapp_ratings > 0" class="space-y-2">
                        <div v-for="rating in [5, 4, 3, 2, 1]" :key="'whatsapp-dist-'+rating" class="flex items-center">
                            <span class="w-8 flex items-center text-sm">
                                <span class="mr-1">{{ rating }}</span>
                                <span class="text-yellow-400">★</span>
                            </span>
                            <div class="flex-1 h-3 bg-gray-200 rounded-full mx-3">
                                <div
                                    class="h-3 bg-gradient-to-r from-green-400 to-green-500 rounded-full transition-all duration-500 ease-out rating-bar"
                                    :style="{ width: `${((whatsapp_rating_distribution[rating] || 0) / total_whatsapp_ratings) * 100}%` }"
                                ></div>
                            </div>
                            <span class="w-8 text-sm text-gray-600">{{ whatsapp_rating_distribution[rating] || 0 }}</span>
                        </div>
                    </div>
                    <div v-else class="text-sm text-gray-500 text-center py-4">{{ __('No ratings yet') }}</div>
                </div>

                <!-- Recent WhatsApp Ratings -->
                <div>
                    <h3 class="font-medium text-gray-900 mb-3">{{ __('Recent WhatsApp Ratings') }}</h3>
                    <div v-if="filteredWhatsappRatings && filteredWhatsappRatings.length" class="max-h-64 overflow-y-auto space-y-3 pr-2 scrollbar-hide">
                        <div v-for="rating in filteredWhatsappRatings" :key="'whatsapp-rating-'+rating.id"
                             class="bg-gray-50 rounded-lg p-3 border border-gray-100 hover:shadow-md transition-shadow duration-200">
                            <div class="flex items-start">
                                <div class="flex-shrink-0 mr-3 mt-1">
                                    <div class="w-8 h-8 rounded-full bg-green-600 flex items-center justify-center text-white text-sm">
                                        <icon name="phone" class="h-4 w-4" />
                                    </div>
                                </div>
                                <div class="flex-1">
                                    <div class="flex items-center justify-between mb-1">
                                        <span class="text-yellow-400 flex text-sm">
                                            <template v-for="star in 5" :key="'wa-'+rating.id+'-'+star">
                                                <span v-if="star <= rating.rating" class="text-yellow-400">★</span>
                                                <span v-else class="text-gray-300">★</span>
                                            </template>
                                        </span>
                                        <span class="text-xs text-gray-500">{{ formatDate(rating.created_at) }}</span>
                                    </div>
                                    <p class="text-xs text-gray-600 mb-1">{{ __('From') }}: {{ rating.whatsapp_number }}</p>
                                    <p class="text-sm text-gray-700">{{ rating.comment }}</p>
                                    <div v-if="rating.category || rating.agent_id" class="text-xs text-gray-500 mt-1">
                                        <span v-if="rating.category">{{ __('Category') }}: {{ rating.category }}</span>
                                        <span v-if="rating.agent_id"> • {{ __('Agent') }}: {{ rating.agent_id }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <p v-else class="text-sm text-gray-500 text-center py-4">{{ __('No recent ratings') }}</p>
                </div>
            </div>
        </section>

        <!-- Feedback Trends Section -->
        <section class="bg-white rounded-xl shadow-lg p-6 mb-8">
            <h2 class="font-semibold text-xl mb-6 text-gray-900">{{ __('Feedback Trends') }}</h2>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div class="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg border border-blue-100">
                    <div class="flex items-center">
                        <div class="p-3 rounded-lg bg-blue-500 text-white mr-4">
                            <icon name="chat" class="h-6 w-6" />
                        </div>
                        <div>
                            <p class="text-sm text-gray-600">{{ __('Total Ratings') }}</p>
                            <h3 class="text-2xl font-bold text-gray-900">{{ totalRatingsCount }}</h3>
                        </div>
                    </div>
                </div>
                <div class="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-lg border border-green-100">
                    <div class="flex items-center">
                        <div class="p-3 rounded-lg bg-green-500 text-white mr-4">
                            <icon name="aperture" class="h-6 w-6" />
                        </div>
                        <div>
                            <p class="text-sm text-gray-600">{{ __('Average Rating') }}</p>
                            <h3 class="text-2xl font-bold text-gray-900">{{ overallAverageRating }}/5</h3>
                        </div>
                    </div>
                </div>
                <div class="bg-gradient-to-r from-purple-50 to-violet-50 p-6 rounded-lg border border-purple-100">
                    <div class="flex items-center">
                        <div class="p-3 rounded-lg bg-purple-500 text-white mr-4">
                            <icon name="plus" class="h-6 w-6" />
                        </div>
                        <div>
                            <p class="text-sm text-gray-600">{{ __('Positive Feedback') }}</p>
                            <h3 class="text-2xl font-bold text-gray-900">{{ positiveFeedbackPercentage }}%</h3>
                        </div>
                    </div>
                </div>
            </div>
        </section>

       <!-- Response Management Section -->
<section class="bg-white rounded-xl shadow-lg p-6">
    <h2 class="font-semibold text-xl mb-6 text-gray-900">{{ __('Response Management') }}</h2>
    <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
            <thead>
                <tr>
                    <th class="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ __('Customer') }}</th>
                    <th class="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ __('Rating') }}</th>
                    <th class="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ __('Channel') }}</th>
                    <th class="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ __('Feedback') }}</th>
                    <th class="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ __('Date') }}</th>
                    <th class="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ __('Actions') }}</th>
                </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
                <tr v-for="item in paginatedResponseManagement" :key="'response-'+item.id" class="hover:bg-gray-50">
                    <td class="px-6 py-4 whitespace-nowrap">
                        <div class="flex items-center">
                            <div class="flex-shrink-0 h-10 w-10">
                                <div class="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-bold text-sm">
                                    {{ item.initials || 'U' }}
                                </div>
                            </div>
                            <div class="ml-4">
                                <div class="text-sm font-medium text-gray-900">{{ item.customer }}</div>
                                <div class="text-sm text-gray-500">{{ item.email }}</div>
                            </div>
                        </div>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap">
                        <div class="flex text-yellow-400">
                            <template v-for="star in 5" :key="'table-'+item.id+'-'+star">
                                <span v-if="star <= item.rating" class="text-yellow-400">★</span>
                                <span v-else class="text-gray-300">★</span>
                            </template>
                        </div>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap">
                        <span class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                              :class="{
                                  'bg-green-100 text-green-800': item.channel === 'whatsapp',
                                  'bg-blue-100 text-blue-800': item.channel === 'chat',
                                  'bg-purple-100 text-purple-800': item.channel === 'ticket'
                              }">
                            {{ item.channel }}
                        </span>
                    </td>
                    <td class="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">{{ item.feedback }}</td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ item.date }}</td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button class="text-indigo-600 hover:text-indigo-900 mr-3">{{ __('Reply') }}</button>
                        <button class="text-red-600 hover:text-red-900">{{ __('Archive') }}</button>
                    </td>
                </tr>
                <tr v-if="paginatedResponseManagement.length === 0">
                    <td colspan="6" class="px-6 py-4 text-center text-sm text-gray-500">
                        {{ __('No feedback found') }}
                    </td>
                </tr>
            </tbody>
        </table>
    </div>

    <!-- Pagination -->

<!-- Change the pagination section to use server-side data -->
<div v-if="totalPages > 1" class="mt-6 flex items-center justify-between">
    <div class="text-sm text-gray-700">
        {{ __('Showing') }} <span class="font-medium">{{ (currentPage - 1) * perPage + 1 }}</span>
        {{ __('to') }} <span class="font-medium">{{ Math.min(currentPage * perPage, response_management_total) }}</span>
        {{ __('of') }} <span class="font-medium">{{ response_management_total }}</span> {{ __('results') }}
    </div>
    <div class="flex space-x-2">
        <button
            @click="changePage(currentPage - 1)"
            :disabled="currentPage === 1"
            class="px-3 py-1 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
            {{ __('Previous') }}
        </button>
        <button
            v-for="page in totalPages"
            :key="'page-'+page"
            @click="changePage(page)"
            :class="[
                'px-3 py-1 rounded-md border text-sm font-medium',
                currentPage === page
                    ? 'border-indigo-500 bg-indigo-500 text-white'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
            ]"
        >
            {{ page }}
        </button>
        <button
            @click="changePage(currentPage + 1)"
            :disabled="currentPage === totalPages"
            class="px-3 py-1 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
            {{ __('Next') }}
        </button>
    </div>
</div>
</section>
    </div>
</template>

<script>
import { Head } from '@inertiajs/vue3'
import Layout from '@/Shared/Layout'
import Icon from '@/Shared/Icon'

export default {
    metaInfo: { title: 'Customer Feedback' },
    components: {
        Head,
        Icon,
    },
    layout: Layout,
    props: {
        // Chat rating props
        average_chat_rating: Number,
        chat_rating_distribution: Object,
        total_chat_ratings: Number,
        recent_chat_ratings: Array,

        // Ticket reviews props
        average_ticket_rating: Number,
        ticket_rating_distribution: Object,
        total_ticket_reviews: Number,
        recent_ticket_reviews: Array,

        // WhatsApp ratings props
        whatsapp_average_rating: Number,
        whatsapp_rating_distribution: Object,
        total_whatsapp_ratings: Number,
        recent_whatsapp_ratings: Array,

         // Response management data
        response_management_data: Array,
        response_management_total: Number,
        response_management_current_page: Number,
        response_management_per_page: Number,

          filters: Object,
    },
    data() {
        return {
         searchFilter: this.$props.filters?.search || '',
        selectedRatingFilter: this.$props.filters?.rating || 'all',
        selectedChannelFilter: this.$props.filters?.channel || 'all',
        currentPage: this.response_management_current_page || 1,
        perPage: this.response_management_per_page || 5,

         loading: false,
        }
    },
    computed: {
        reloadData() {
        this.loading = true;
        this.$inertia.get(route('feedback'), {
            page: this.currentPage,
            rating: this.selectedRatingFilter,
            channel: this.selectedChannelFilter,
            search: this.searchFilter,
        }, {
            preserveState: true,
            replace: true,
            onFinish: () => {
                this.loading = false;
            }
        });
    },
        filteredChatRatings() {
            return this.filterRatings(this.recent_chat_ratings || [], 'chat');
        },

        filteredTicketReviews() {
            return this.filterRatings(this.recent_ticket_reviews || [], 'ticket');
        },

        filteredWhatsappRatings() {
            return this.filterRatings(this.recent_whatsapp_ratings || [], 'whatsapp');
        },

       // Server-side paginated data
    paginatedResponseManagement() {
        return this.response_management_data || [];
    },

    totalPages() {
        return Math.ceil((this.response_management_total || 0) / this.perPage);
    },


        totalRatingsCount() {
            return (this.total_chat_ratings || 0) + (this.total_ticket_reviews || 0) + (this.total_whatsapp_ratings || 0);
        },

        overallAverageRating() {
            const chatAvg = this.average_chat_rating || 0;
            const ticketAvg = this.average_ticket_rating || 0;
            const whatsappAvg = this.whatsapp_average_rating || 0;

            const totalRatings = (this.total_chat_ratings || 0) + (this.total_ticket_reviews || 0) + (this.total_whatsapp_ratings || 0);

            if (totalRatings === 0) return '0.0';

            const weightedSum =
                (chatAvg * (this.total_chat_ratings || 0)) +
                (ticketAvg * (this.total_ticket_reviews || 0)) +
                (whatsappAvg * (this.total_whatsapp_ratings || 0));

            return (weightedSum / totalRatings).toFixed(1);
        },

        positiveFeedbackPercentage() {
            // Calculate percentage of ratings 4 stars and above
            const chatPositive = this.calculatePositivePercentage(this.chat_rating_distribution, this.total_chat_ratings);
            const ticketPositive = this.calculatePositivePercentage(this.ticket_rating_distribution, this.total_ticket_reviews);
            const whatsappPositive = this.calculatePositivePercentage(this.whatsapp_rating_distribution, this.total_whatsapp_ratings);

            const totalPositive = chatPositive + ticketPositive + whatsappPositive;
            const totalChannels = [chatPositive, ticketPositive, whatsappPositive].filter(p => p > 0).length;

            return totalChannels > 0 ? Math.round(totalPositive / totalChannels) : 0;
        }
    },
    methods: {
          reloadData() {
        this.$inertia.get(route('feedback'), {
            page: this.currentPage,
            rating: this.selectedRatingFilter,
            channel: this.selectedChannelFilter,
            search: this.searchFilter,
        }, {
            preserveState: true,
            replace: true,
        });
    },

    changePage(page) {
        this.currentPage = page;
        this.reloadData();
    },
        filterRatings(ratings, channel = null) {
            if (!ratings) return [];

            return ratings.filter(rating => {
                // Search filter
                const matchesSearch = !this.searchFilter ||
                    (rating.customer && rating.customer.toLowerCase().includes(this.searchFilter.toLowerCase())) ||
                    (rating.comment && rating.comment.toLowerCase().includes(this.searchFilter.toLowerCase())) ||
                    (rating.feedback && rating.feedback.toLowerCase().includes(this.searchFilter.toLowerCase())) ||
                    (rating.whatsapp_number && rating.whatsapp_number.toLowerCase().includes(this.searchFilter.toLowerCase()));

                // Rating filter
                let matchesRating = true;
                if (this.selectedRatingFilter !== 'all') {
                    const ratingValue = parseInt(this.selectedRatingFilter);
                    matchesRating = rating.rating === ratingValue;
                }

                // Channel filter
                let matchesChannel = true;
                if (this.selectedChannelFilter !== 'all' && channel) {
                    matchesChannel = channel === this.selectedChannelFilter;
                } else if (this.selectedChannelFilter !== 'all' && rating.channel) {
                    matchesChannel = rating.channel === this.selectedChannelFilter;
                }

                return matchesSearch && matchesRating && matchesChannel;
            });
        },

        formatDate(dateString) {
            if (!dateString) return '';
            const options = { year: 'numeric', month: 'short', day: 'numeric' };
            return new Date(dateString).toLocaleDateString(undefined, options);
        },

        calculatePositivePercentage(distribution, total) {
            if (!distribution || !total || total === 0) return 0;

            const positiveRatings = (distribution[4] || 0) + (distribution[5] || 0);
            return Math.round((positiveRatings / total) * 100);
        }
    },
    mounted() {
        // Animate rating bars on component mount
        setTimeout(() => {
            const ratingBars = document.querySelectorAll('.rating-bar');
            ratingBars.forEach(bar => {
                const width = bar.style.width;
                bar.style.width = '0';
                setTimeout(() => {
                    bar.style.width = width;
                }, 100);
            });
        }, 300);
    },
    watch: {
    searchFilter() {
        this.currentPage = 1;
        this.reloadData();
    },
    selectedRatingFilter() {
        this.currentPage = 1;
        this.reloadData();
    },
    selectedChannelFilter() {
        this.currentPage = 1;
        this.reloadData();
    },
},
}
</script>

<style scoped>
.scrollbar-hide::-webkit-scrollbar {
    display: none;
}

.scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
}

.rating-bar {
    transition: width 0.5s ease-in-out;
}

</style>
